// Bloques H/I/J/K/N/O/P de 008 (Fase 3, Tanda 18, ver PLAN.md). Un solo
// componente, insertado de una vez en `Formulario008.tsx` después de
// `Bloques008Ingreso` (B-G) y antes de L/M (diagnósticos).
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AreaDictado } from './AreaDictado';
import { CampoDictado } from './CampoDictado';
import { ChecklistGrid } from './ChecklistGrid';
import {
  CONDICION_EGRESO_008_OPCIONES,
  EXAMENES_COMPLEMENTARIOS_OPCIONES,
  ITEMS_EXAMEN_FISICO_008,
  TOPE_FILAS_MEDICAMENTOS_008,
  filaMedicamento008Vacia,
  type EstadoBloqueJ008,
  type EstadoBloqueK008,
  type EstadoBloqueO008,
  type EstadoBloques008Clinico,
  type ExamenFisico008,
  type FilaMedicamento008,
  type ItemExamenFisico008,
} from './bloques008ClinicoDatos';

function CampoTexto({ id, label, value, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void }): JSX.Element {
  return <CampoDictado id={id} etiqueta={label} valor={value} onChange={onChange} />;
}

function CampoNumero({ id, label, value, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="number" min={0} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function CampoCheckbox({ id, label, checked, onChange }: { readonly id: string; readonly label: string; readonly checked: boolean; readonly onChange: (v: boolean) => void }): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
    </div>
  );
}

export interface Bloques008ClinicoProps {
  readonly estado: EstadoBloques008Clinico;
  readonly onChange: (estado: EstadoBloques008Clinico) => void;
}

function BloqueH({ estado, onChange }: { readonly estado: ExamenFisico008; readonly onChange: (v: ExamenFisico008) => void }): JSX.Element {
  const { t } = useTranslation();

  function actualizarItem(item: ItemExamenFisico008, cambios: Partial<ExamenFisico008[ItemExamenFisico008]>): void {
    onChange({ ...estado, [item]: { ...estado[item], ...cambios } });
  }

  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f008.bloqueH.titulo')}</h4>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {ITEMS_EXAMEN_FISICO_008.map((item) => (
          <div key={item} className="flex flex-col gap-1 rounded-md border border-border p-2">
            <CampoCheckbox id={`f008-h-${item}`} label={t(`formularios.f008.bloqueH.items.${item}`)} checked={estado[item].presente} onChange={(v) => actualizarItem(item, { presente: v })} />
            <Input
              aria-label={t('formularios.f008.bloqueH.descripcion')}
              placeholder={t('formularios.f008.bloqueH.descripcion')}
              value={estado[item].descripcion}
              onChange={(e) => actualizarItem(item, { descripcion: e.target.value })}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function BloqueI({ estado, onChange }: { readonly estado: string; readonly onChange: (v: string) => void }): JSX.Element {
  const { t } = useTranslation();
  return <AreaDictado id="f008-i-trauma-critico" etiqueta={t('formularios.f008.bloqueI.titulo')} valor={estado} onChange={onChange} />;
}

function BloqueJ({ estado, onChange }: { readonly estado: EstadoBloqueJ008; readonly onChange: (v: EstadoBloqueJ008) => void }): JSX.Element {
  const { t } = useTranslation();

  function actualizar(cambios: Partial<EstadoBloqueJ008>): void {
    onChange({ ...estado, ...cambios });
  }

  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f008.bloqueJ.titulo')}</h4>
      <CampoCheckbox id="f008-j-no-aplica" label={t('formularios.f008.bloqueJ.noAplica')} checked={estado.noAplica} onChange={(v) => actualizar({ noAplica: v })} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
        <CampoNumero id="f008-j-gestas" label={t('formularios.f008.bloqueJ.numeroGestas')} value={estado.numeroGestas} onChange={(v) => actualizar({ numeroGestas: v })} />
        <CampoNumero id="f008-j-partos" label={t('formularios.f008.bloqueJ.numeroPartos')} value={estado.numeroPartos} onChange={(v) => actualizar({ numeroPartos: v })} />
        <CampoNumero id="f008-j-abortos" label={t('formularios.f008.bloqueJ.numeroAbortos')} value={estado.numeroAbortos} onChange={(v) => actualizar({ numeroAbortos: v })} />
        <CampoNumero id="f008-j-cesareas" label={t('formularios.f008.bloqueJ.numeroCesareas')} value={estado.numeroCesareas} onChange={(v) => actualizar({ numeroCesareas: v })} />
        <div className="flex flex-col gap-1">
          <Label htmlFor="f008-j-fum">{t('formularios.f008.bloqueJ.fum')}</Label>
          <Input id="f008-j-fum" type="date" value={estado.fum} onChange={(e) => actualizar({ fum: e.target.value })} />
        </div>
        <CampoTexto id="f008-j-semanas-gestacion" label={t('formularios.f008.bloqueJ.semanasGestacion')} value={estado.semanasGestacion} onChange={(v) => actualizar({ semanasGestacion: v })} />
        <CampoTexto id="f008-j-movimiento-fetal" label={t('formularios.f008.bloqueJ.movimientoFetal')} value={estado.movimientoFetal} onChange={(v) => actualizar({ movimientoFetal: v })} />
        <CampoTexto id="f008-j-fcf" label={t('formularios.f008.bloqueJ.frecuenciaCardiacaFetal')} value={estado.frecuenciaCardiacaFetal} onChange={(v) => actualizar({ frecuenciaCardiacaFetal: v })} />
        <CampoTexto id="f008-j-ruptura-membranas" label={t('formularios.f008.bloqueJ.rupturaMembranas')} value={estado.rupturaMembranas} onChange={(v) => actualizar({ rupturaMembranas: v })} />
        <CampoTexto id="f008-j-tiempo" label={t('formularios.f008.bloqueJ.tiempo')} value={estado.tiempo} onChange={(v) => actualizar({ tiempo: v })} />
        <CampoTexto id="f008-j-afu" label={t('formularios.f008.bloqueJ.afu')} value={estado.afu} onChange={(v) => actualizar({ afu: v })} />
        <CampoTexto id="f008-j-presentacion" label={t('formularios.f008.bloqueJ.presentacion')} value={estado.presentacion} onChange={(v) => actualizar({ presentacion: v })} />
        <CampoTexto id="f008-j-dilatacion" label={t('formularios.f008.bloqueJ.dilatacion')} value={estado.dilatacion} onChange={(v) => actualizar({ dilatacion: v })} />
        <CampoTexto id="f008-j-borramiento" label={t('formularios.f008.bloqueJ.borramiento')} value={estado.borramiento} onChange={(v) => actualizar({ borramiento: v })} />
        <CampoTexto id="f008-j-plano" label={t('formularios.f008.bloqueJ.plano')} value={estado.plano} onChange={(v) => actualizar({ plano: v })} />
        <CampoTexto id="f008-j-pelvis-viable" label={t('formularios.f008.bloqueJ.pelvisViable')} value={estado.pelvisViable} onChange={(v) => actualizar({ pelvisViable: v })} />
        <CampoTexto id="f008-j-sangrado-vaginal" label={t('formularios.f008.bloqueJ.sangradoVaginal')} value={estado.sangradoVaginal} onChange={(v) => actualizar({ sangradoVaginal: v })} />
        <CampoTexto id="f008-j-contracciones" label={t('formularios.f008.bloqueJ.contracciones')} value={estado.contracciones} onChange={(v) => actualizar({ contracciones: v })} />
        <CampoTexto id="f008-j-score-mama" label={t('formularios.f008.bloqueJ.scoreMama')} value={estado.scoreMama} onChange={(v) => actualizar({ scoreMama: v })} />
      </div>
      <AreaDictado id="f008-j-descripcion" etiqueta={t('formularios.f008.bloqueJ.descripcion')} valor={estado.descripcion} onChange={(v) => actualizar({ descripcion: v })} filas={3} />
    </div>
  );
}

function BloqueK({ estado, onChange }: { readonly estado: EstadoBloqueK008; readonly onChange: (v: EstadoBloqueK008) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f008.bloqueK.titulo')}</h4>
      <CampoCheckbox id="f008-k-no-aplica" label={t('formularios.f008.bloqueK.noAplica')} checked={estado.noAplica} onChange={(v) => onChange({ ...estado, noAplica: v })} />
      <ChecklistGrid claves={EXAMENES_COMPLEMENTARIOS_OPCIONES} prefijoI18n="formularios.f008.bloqueK.examenes.opciones" prefijoId="f008-k" valores={estado.examenes} onChange={(v) => onChange({ ...estado, examenes: v })} />
      <AreaDictado id="f008-k-observaciones" etiqueta={t('formularios.f008.bloqueK.observaciones')} valor={estado.observaciones} onChange={(v) => onChange({ ...estado, observaciones: v })} />
    </div>
  );
}

function BloqueN({
  medicamentos,
  onChangeMedicamentos,
  indicacionesGenerales,
  onChangeIndicacionesGenerales,
}: {
  readonly medicamentos: readonly FilaMedicamento008[];
  readonly onChangeMedicamentos: (v: readonly FilaMedicamento008[]) => void;
  readonly indicacionesGenerales: string;
  readonly onChangeIndicacionesGenerales: (v: string) => void;
}): JSX.Element {
  const { t } = useTranslation();

  function actualizarFila(id: string, campo: keyof Omit<FilaMedicamento008, 'id'>, valor: string): void {
    onChangeMedicamentos(medicamentos.map((f) => (f.id === id ? { ...f, [campo]: valor } : f)));
  }

  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f008.bloqueN.titulo')}</h4>
      <div className="flex flex-col gap-2">
        {medicamentos.map((f) => (
          <div key={f.id} className="grid grid-cols-1 gap-2 rounded-md border border-border p-3 sm:grid-cols-6 sm:items-end">
            <CampoTexto id={`f008-n-${f.id}-medicamento`} label={t('formularios.f008.bloqueN.medicamento')} value={f.medicamento} onChange={(v) => actualizarFila(f.id, 'medicamento', v)} />
            <CampoTexto id={`f008-n-${f.id}-via`} label={t('formularios.f008.bloqueN.via')} value={f.via} onChange={(v) => actualizarFila(f.id, 'via', v)} />
            <CampoTexto id={`f008-n-${f.id}-dosis`} label={t('formularios.f008.bloqueN.dosis')} value={f.dosis} onChange={(v) => actualizarFila(f.id, 'dosis', v)} />
            <CampoTexto id={`f008-n-${f.id}-posologia`} label={t('formularios.f008.bloqueN.posologia')} value={f.posologia} onChange={(v) => actualizarFila(f.id, 'posologia', v)} />
            <CampoTexto id={`f008-n-${f.id}-dias`} label={t('formularios.f008.bloqueN.dias')} value={f.dias} onChange={(v) => actualizarFila(f.id, 'dias', v)} />
            <Button type="button" variant="ghost" size="sm" className="w-fit" onClick={() => onChangeMedicamentos(medicamentos.filter((row) => row.id !== f.id))}>
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" className="w-fit" disabled={medicamentos.length >= TOPE_FILAS_MEDICAMENTOS_008} onClick={() => onChangeMedicamentos([...medicamentos, filaMedicamento008Vacia()])}>
          {t('formularios.f008.bloqueN.agregarFila')}
        </Button>
      </div>
      <AreaDictado id="f008-n-indicaciones-generales" etiqueta={t('formularios.f008.bloqueN.indicacionesGenerales')} valor={indicacionesGenerales} onChange={onChangeIndicacionesGenerales} />
    </div>
  );
}

function BloqueO({ estado, onChange }: { readonly estado: EstadoBloqueO008; readonly onChange: (v: EstadoBloqueO008) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f008.bloqueO.titulo')}</h4>
      <ChecklistGrid claves={CONDICION_EGRESO_008_OPCIONES} prefijoI18n="formularios.f008.bloqueO.condicion.opciones" prefijoId="f008-o" valores={estado.condicion} onChange={(v) => onChange({ ...estado, condicion: v })} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <CampoTexto id="f008-o-establecimiento" label={t('formularios.f008.bloqueO.establecimiento')} value={estado.establecimiento} onChange={(v) => onChange({ ...estado, establecimiento: v })} />
        <CampoNumero id="f008-o-dias-reposo" label={t('formularios.f008.bloqueO.diasReposo')} value={estado.diasReposo} onChange={(v) => onChange({ ...estado, diasReposo: v })} />
      </div>
      <AreaDictado id="f008-o-observaciones" etiqueta={t('formularios.f008.bloqueO.observaciones')} valor={estado.observaciones} onChange={(v) => onChange({ ...estado, observaciones: v })} />
    </div>
  );
}

function BloqueP(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f008.bloqueP.titulo')}</h4>
      <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
    </div>
  );
}

export function Bloques008Clinico({ estado, onChange }: Bloques008ClinicoProps): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <BloqueH estado={estado.h} onChange={(v) => onChange({ ...estado, h: v })} />
      <BloqueI estado={estado.examenTraumaCritico} onChange={(v) => onChange({ ...estado, examenTraumaCritico: v })} />
      <BloqueJ estado={estado.j} onChange={(v) => onChange({ ...estado, j: v })} />
      <BloqueK estado={estado.k} onChange={(v) => onChange({ ...estado, k: v })} />
      <BloqueN
        medicamentos={estado.medicamentos}
        onChangeMedicamentos={(v) => onChange({ ...estado, medicamentos: v })}
        indicacionesGenerales={estado.indicacionesGenerales}
        onChangeIndicacionesGenerales={(v) => onChange({ ...estado, indicacionesGenerales: v })}
      />
      <BloqueO estado={estado.o} onChange={(v) => onChange({ ...estado, o: v })} />
      <BloqueP />
    </div>
  );
}
